import { useState, useEffect } from 'react';

const STORAGE_KEY = 'MW_EXPANDED_SETTINGS';

const useExpandedWatchlist = (allColumns) => {
  // Initialize state from localStorage or defaults
  const [columnSettings, setColumnSettings] = useState(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        // Ensure we handle cases where 'allColumns' might have changed (new fields added)
        // Merge saved order/visibility with current set of fields
        const savedOrder = parsed.columnOrder || [];
        const savedHidden = parsed.hiddenColumns || [];
        
        // Final order: saved items first, followed by any new ones not in the saved list
        const currentFieldNames = allColumns.map(col => col.field);
        const filteredSavedOrder = savedOrder.filter(field => currentFieldNames.includes(field));
        const newFields = currentFieldNames.filter(field => !savedOrder.includes(field));
        
        return {
          columnOrder: [...filteredSavedOrder, ...newFields],
          hiddenColumns: savedHidden.filter(field => currentFieldNames.includes(field))
        };
      } catch (e) {
        console.error("Error parsing expanded watchlist settings:", e);
      }
    }
    
    // Default: all visible, original order
    return {
      columnOrder: allColumns.map(col => col.field),
      hiddenColumns: []
    };
  });

  // Save to localStorage whenever settings change
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(columnSettings));
  }, [columnSettings]);

  const toggleColumn = (field) => {
    setColumnSettings(prev => {
      const isHidden = prev.hiddenColumns.includes(field);
      return {
        ...prev,
        hiddenColumns: isHidden 
          ? prev.hiddenColumns.filter(f => f !== field)
          : [...prev.hiddenColumns, field]
      };
    });
  };

  const reorderColumns = (startIndex, endIndex) => {
    setColumnSettings(prev => {
      // reorder logic:
      const result = Array.from(prev.columnOrder);
      const [removedItem] = result.splice(startIndex, 1);
      result.splice(endIndex, 0, removedItem);
      
      return {
        ...prev,
        columnOrder: result
      };
    });
  };

  const resetSettings = () => {
    setColumnSettings({
      columnOrder: allColumns.map(col => col.field),
      hiddenColumns: []
    });
  };

  // Compute all columns in their current custom order for the Popover
  const orderedAllColumns = columnSettings.columnOrder
    .map(field => allColumns.find(col => col.field === field))
    .filter(Boolean);

  // Compute only visible columns for the Table
  const tableColumns = orderedAllColumns
    .filter(col => !columnSettings.hiddenColumns.includes(col.field));

  return {
    tableColumns,
    allColumns: orderedAllColumns,
    hiddenColumns: columnSettings.hiddenColumns,
    toggleColumn,
    reorderColumns,
    resetSettings
  };
};

export default useExpandedWatchlist;
